/**
 * campushubai.com — the public marketing domain.
 *
 * It serves the SAME build as campushub.bytego.ai (its own CloudFront
 * distribution, same S3 bucket), but only the marketing website lives there.
 * The app — login, dashboard, everything behind auth — stays on
 * campushub.bytego.ai, because the refresh cookie is scoped to `.bytego.ai`
 * and the API's CORS allowlist is built around that host. Serving /login on
 * campushubai.com would render a form that can never keep a session.
 *
 * Hard loads of app paths are 301'd at the edge (CloudFront Function
 * `campushubai-com-site`). This module covers the other way in: client-side
 * navigation from a marketing page (the header's "Sign in" link), which never
 * reaches CloudFront. Keep MARKETING_EXACT / MARKETING_PREFIXES in step with
 * that function.
 */

export const APP_ORIGIN = "https://campushub.bytego.ai";

const MARKETING_HOSTS = new Set(["campushubai.com", "www.campushubai.com"]);

const MARKETING_EXACT = new Set([
  "/", "/platform", "/integrations", "/ai", "/solutions", "/implementation",
  "/roadmap", "/customers", "/pricing", "/why-campushub", "/security",
  "/coming-soon", "/philosophy", "/about", "/bytego", "/careers", "/partners",
  "/resources", "/faq", "/contact", "/demo", "/support", "/tour", "/home",
  // Legacy marketing paths — router.tsx redirects them client-side.
  "/product", "/features", "/experience", "/hubba", "/face-scan",
  "/smart-campus", "/robotics", "/creative-studio", "/ai-timetable",
  "/higher-education",
]);

const MARKETING_PREFIXES = [
  "/platform/", "/products/", "/solutions/", "/customers/", "/resources/", "/legal/",
];

/** True when this document was served from the marketing domain. */
export function isMarketingDomain(): boolean {
  if (typeof window === "undefined") return false;
  return MARKETING_HOSTS.has(window.location.hostname.toLowerCase());
}

/** Whether `pathname` is a marketing page (allowed on the marketing domain). */
export function isMarketingPath(pathname: string): boolean {
  // Case-insensitive: React Router matches `/Pricing` and CloudFront serves it
  // from the same build, so `/PLATFORM/x` is as much a marketing page as `/platform/x`.
  const path = (pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname).toLowerCase();
  if (MARKETING_EXACT.has(path)) return true;
  return MARKETING_PREFIXES.some((p) => path.startsWith(p));
}

/**
 * Absolute app URL for `path` when on the marketing domain, otherwise `null`
 * (meaning: use normal in-app routing).
 */
export function appHrefOnMarketingDomain(path: string): string | null {
  return isMarketingDomain() ? `${APP_ORIGIN}${path}` : null;
}
